import {
  ImportedProject_Read,
  postV1CompaniesCompanyIdImportsProjects,
  ProjectImportRequest,
  WaypointType,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';

import { CSVColumn } from '~utils/csv/csvProcessor';

import {
  buildImportSite,
  numberOrUndefined,
  trimmedOrUndefined,
  withoutEmptyValues,
} from './importPayload';
import { EntityCSVConfig } from './types';
import { normalizeUnitOfMeasure, VALID_ORDER_UNITS_OF_MEASURE } from './unitOfMeasure';

const projectsColumns: CSVColumn[] = [
  {
    key: 'external_id',
    header: 'external_id',
    required: false,
    exampleValue: 'PROJ-001',
    description:
      "Unique ID for the project. Provided = re-import updates the same project and replaces the project's materials and phases with the ones in the CSV. Blank = a new ID is generated and re-uploads create duplicates.",
  },
  {
    key: 'project_name',
    header: 'project_name',
    required: true,
    exampleValue: 'Main St resurfacing',
    description: 'Name of the project',
  },
  {
    key: 'customer_name',
    header: 'customer_name',
    required: true,
    exampleValue: 'Acme Paving',
    description: 'Customer account name. Created when no match is found.',
  },
  {
    key: 'customer_external_id',
    header: 'customer_external_id',
    required: false,
    exampleValue: 'CUST-001',
    description: 'Your internal reference ID for the customer',
  },
  {
    key: 'effective_at',
    header: 'effective_at',
    required: false,
    exampleValue: '2026-08-10',
    description: 'When the project becomes effective',
  },
  {
    key: 'expires_at',
    header: 'expires_at',
    required: false,
    exampleValue: '2026-12-31',
    description: 'When the project expires',
  },
  {
    key: 'dispatch_number',
    header: 'dispatch_number',
    required: false,
    exampleValue: '',
    description: 'Dispatch number of the project',
  },
  {
    key: 'po_job_number',
    header: 'po_job_number',
    required: false,
    exampleValue: '',
    description: 'PO or job number of the project',
  },
  {
    key: 'phase_name',
    header: 'phase_name',
    required: false,
    exampleValue: 'Base course',
    description: 'Name of the single phase created for the project',
  },
  {
    key: 'phase_code',
    header: 'phase_code',
    required: false,
    exampleValue: 'PH-01',
    description: 'Code of the phase; required when phase_name is set',
  },
  {
    key: 'material_name',
    header: 'material_name',
    required: false,
    exampleValue: '3/4 Crushed Stone',
    description: 'Material name. Created when no match is found.',
  },
  {
    key: 'material_quantity',
    header: 'material_quantity',
    required: false,
    exampleValue: '2500',
    description: 'Total quantity of the material for the project',
  },
  {
    key: 'material_unit_of_measure',
    header: 'material_unit_of_measure',
    required: false,
    exampleValue: 'Tonne',
    description: 'Unit for the material quantity, for example Tonne or Yard',
  },
  {
    key: 'pickup_site_name',
    header: 'pickup_site_name',
    required: false,
    exampleValue: 'North Quarry',
    description: 'Pickup site name. Created when no match is found.',
  },
  {
    key: 'pickup_site_external_id',
    header: 'pickup_site_external_id',
    required: false,
    exampleValue: 'SITE-NQ-01',
    description:
      'Your unique identifier for the pickup site; used to match an existing site',
  },
  {
    key: 'pickup_site_address',
    header: 'pickup_site_address',
    required: false,
    exampleValue: '123 Quarry Rd, Austin, TX 78701',
    description: 'Full address used to geocode a newly created pickup site',
  },
  {
    key: 'dropoff_site_name',
    header: 'dropoff_site_name',
    required: false,
    exampleValue: 'Main St Job Site',
    description: 'Dropoff site name. Created when no match is found.',
  },
  {
    key: 'dropoff_site_external_id',
    header: 'dropoff_site_external_id',
    required: false,
    exampleValue: 'SITE-MS-01',
    description:
      'Your unique identifier for the drop-off site; used to match an existing site',
  },
  {
    key: 'dropoff_site_address',
    header: 'dropoff_site_address',
    required: false,
    exampleValue: '500 Main St, Austin, TX 78701',
    description: 'Full address used to geocode a newly created dropoff site',
  },
  {
    key: 'foreman_email',
    header: 'foreman_email',
    required: false,
    exampleValue: '',
    description: "Must match an existing user's email; unmatched emails are skipped",
  },
  {
    key: 'notes',
    header: 'notes',
    required: false,
    exampleValue: '',
    description: 'Notes for the project',
  },
  {
    key: 'order_notes',
    header: 'order_notes',
    required: false,
    exampleValue: 'Call foreman on arrival',
    description: 'Notes copied to each order created under the project',
  },
  {
    key: 'job_notes',
    header: 'job_notes',
    required: false,
    exampleValue: 'Use rear gate',
    description: 'Notes copied to each job created under the project',
  },
];

type ProjectImportSite = NonNullable<ProjectImportRequest['sites']>[number];
type ProjectImportMaterial = NonNullable<ProjectImportRequest['materials']>[number];

function buildProjectImportRequest(row: Record<string, string>): ProjectImportRequest {
  const sites = [
    buildImportSite(
      WaypointType.PICKUP,
      row.pickup_site_name,
      row.pickup_site_external_id,
      row.pickup_site_address,
    ),
    buildImportSite(
      WaypointType.DROP_OFF,
      row.dropoff_site_name,
      row.dropoff_site_external_id,
      row.dropoff_site_address,
    ),
  ].filter((site): site is ProjectImportSite => site !== undefined);

  const phaseName = trimmedOrUndefined(row.phase_name);
  const phaseCode = trimmedOrUndefined(row.phase_code);
  const materialName = trimmedOrUndefined(row.material_name);
  const foremanEmail = trimmedOrUndefined(row.foreman_email);

  const request: ProjectImportRequest = {
    // A generated ID keeps the row importable, but re-uploading the same file
    // then creates a second project because there is no key to match on.
    external_id: trimmedOrUndefined(row.external_id) ?? `csv-${crypto.randomUUID()}`,
    name: row.project_name?.trim() ?? '',
    customer: {
      name: row.customer_name?.trim() ?? '',
      ...withoutEmptyValues({
        external_id: trimmedOrUndefined(row.customer_external_id),
      }),
    },
    ...withoutEmptyValues({
      effective_at: trimmedOrUndefined(row.effective_at),
      expires_at: trimmedOrUndefined(row.expires_at),
      dispatch_number: trimmedOrUndefined(row.dispatch_number),
      po_job_number: trimmedOrUndefined(row.po_job_number),
      notes: trimmedOrUndefined(row.notes),
      order_notes: trimmedOrUndefined(row.order_notes),
      job_notes: trimmedOrUndefined(row.job_notes),
    }),
  };

  if (phaseName && phaseCode) {
    request.phases = [{ name: phaseName, code: phaseCode }];
  }

  if (materialName) {
    const material: ProjectImportMaterial = {
      name: materialName,
      ...withoutEmptyValues({
        total_quantity: numberOrUndefined(row.material_quantity),
        unit_of_measure: normalizeUnitOfMeasure(row.material_unit_of_measure),
      }),
    };
    request.materials = [material];
  }

  if (foremanEmail) {
    request.foremen = [{ email: foremanEmail }];
  }

  if (sites.length > 0) {
    request.sites = sites;
  }

  return request;
}

async function importProjectRow(
  companyId: string,
  row: Record<string, string>,
): Promise<ImportedProject_Read> {
  const response = await postV1CompaniesCompanyIdImportsProjects({
    path: { 'company-id': companyId },
    body: buildProjectImportRequest(row),
  });

  return response.data.data;
}

export const createProjectsConfig = (): EntityCSVConfig<ImportedProject_Read> => ({
  entityType: 'projects',
  confirmsColumnMapping: true,
  importInstructions: t('csv_import_export.import_instructions.projects'),
  // Sequential: rows commonly share customers/materials/sites, and imports are upserts by external_id
  importBatchSize: 1,
  apiFieldToColumn: {
    'Project/name': 'project_name',
    'Project/effective_at': 'effective_at',
    'Project/expires_at': 'expires_at',
    'Project/order_notes': 'order_notes',
    'Project/job_notes': 'job_notes',
    'Account/name': 'customer_name',
    'Account/external_id': 'customer_external_id',
    'Phase/name': 'phase_name',
    'Phase/code': 'phase_code',
    'Material/name': 'material_name',
    'ProjectMaterialTotal/total_quantity': 'material_quantity',
    'ProjectMaterialTotal/unit_of_measure': 'material_unit_of_measure',
  },
  columns: projectsColumns,

  create: (companyId: string, row: Record<string, string>) =>
    importProjectRow(companyId, row),

  transformImport: (row: Record<string, string>): Record<string, unknown> =>
    buildProjectImportRequest(row),

  rowLabel: (row: Record<string, string>) =>
    trimmedOrUndefined(row.external_id) ??
    trimmedOrUndefined(row.project_name) ??
    trimmedOrUndefined(row.customer_name),

  validateRow: (row: Record<string, string>): string[] => {
    const errors: string[] = [];

    if (!row.project_name?.trim()) {
      errors.push(
        t('csv_import_export.errors.required_field', { field: 'project_name' }),
      );
    }

    if (!row.customer_name?.trim()) {
      errors.push(
        t('csv_import_export.errors.required_field', { field: 'customer_name' }),
      );
    }

    // Phases require both halves — the API rejects a phase without a code
    if (row.phase_name?.trim() && !row.phase_code?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'phase_code' }));
    }
    if (row.phase_code?.trim() && !row.phase_name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'phase_name' }));
    }

    const dateFields = ['effective_at', 'expires_at'];
    dateFields.forEach((field) => {
      const value = row[field]?.trim();
      if (value && !dayjs(value).isValid()) {
        errors.push(t('csv_import_export.errors.invalid_date', { field }));
      }
    });

    const materialQuantity = row.material_quantity?.trim();
    if (
      materialQuantity &&
      (isNaN(Number(materialQuantity)) || Number(materialQuantity) < 0)
    ) {
      errors.push(
        t('csv_import_export.errors.invalid_number', { field: 'material_quantity' }),
      );
    }

    if (
      row.material_unit_of_measure?.trim() &&
      !normalizeUnitOfMeasure(row.material_unit_of_measure)
    ) {
      errors.push(
        t('csv_import_export.errors.invalid_enum', {
          field: 'material_unit_of_measure',
          values: VALID_ORDER_UNITS_OF_MEASURE.join(', '),
        }),
      );
    }

    return errors;
  },
});
